import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faWrench } from '@fortawesome/free-solid-svg-icons';
import SidebarPageLayout from '../../components/layout/SidebarPageLayout';
import Breadcrumbs from '../../components/layout/Breadcrumbs';
import { useAuth } from '../../components/auth/AuthProvider';
import { fetchCmsUsers } from '../../api/auth';
import { canViewUsers, isSuperAdminRole } from '../../auth/appRoles';
import { toTitleCase } from '../../util/strings';

const ALL_USERS = 'all';

function displayValue(value) {
  const text = String(value ?? '').trim();
  return text || '—';
}

function roleLabel(role) {
  const slug = String(role ?? '').trim().toLowerCase();
  if (slug === 'superadmin') {
    return 'Super Admin';
  }
  if (slug === 'admin') {
    return 'Admin';
  }
  if (slug === 'general') {
    return 'General User';
  }
  return toTitleCase(slug);
}

function formatCreatedAt(value) {
  if (value == null || value === '') {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function Users() {
  const { user, isAuthLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(ALL_USERS);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const allowed = canViewUsers(user?.role_id);
  const isSuper = isSuperAdminRole(user?.role_id);
  const accountName = String(user?.organization ?? '').trim();
  const headerLabel = isSuper
    ? 'USERS'
    : `${accountName ? `${accountName.toUpperCase()} ` : ''}USERS`;
  const showAccount = isSuper && selectedAccount === ALL_USERS;
  const visibleUsers = useMemo(() => {
    if (!isSuper || selectedAccount === ALL_USERS) {
      return users;
    }
    return users.filter((row) => String(row.account_id) === selectedAccount);
  }, [isSuper, selectedAccount, users]);

  useEffect(() => {
    document.title = 'Vivosol Plant Performance Insights Hub | Admin / Users';
  }, []);

  useEffect(() => {
    if (isAuthLoading || !allowed) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const response = await fetchCmsUsers();
        if (!cancelled) {
          setUsers(Array.isArray(response?.users) ? response.users : []);
          setAccounts(Array.isArray(response?.accounts) ? response.accounts : []);
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(error);
          setUsers([]);
          setAccounts([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [allowed, isAuthLoading]);

  if (!isAuthLoading && !allowed) {
    return <Navigate to="/admin/account" replace />;
  }

  return (
    <SidebarPageLayout id="adminUsers">
      <div className="content p-5">
        <h1 className="mb-0">Users</h1>
        <Breadcrumbs />
        <div className="panel p-3">
          <div className="header d-flex justify-content-between pb-3">
            <div className="left d-flex gap-3 align-items-center">
              {isSuper ? (
                <select
                  className="form-select users-account-select"
                  aria-label="Account"
                  value={selectedAccount}
                  onChange={(event) => setSelectedAccount(event.target.value)}
                >
                  <option value={ALL_USERS}>All Users</option>
                  {accounts.map((account) => (
                    <option key={account.account_id} value={String(account.account_id)}>
                      {account.name}
                    </option>
                  ))}
                </select>
              ) : null}
              <span><FontAwesomeIcon icon={faUsers} /> {headerLabel}</span>
            </div>
          </div>
          <div className="body">
            {isAuthLoading || isLoading ? (
              <p className="text-muted mb-0">Loading users…</p>
            ) : loadError ? (
              <p className="text-muted mb-0">Unable to load users.</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <td>Email</td>
                    <td>First Name</td>
                    <td>Last Name</td>
                    <td>Role</td>
                    {showAccount ? <td>Account</td> : null}
                    <td>Status</td>
                    <td>Created</td>
                    <td className="users-row-tool" aria-label="User tools" />
                  </tr>
                </thead>
                <tbody>
                  {visibleUsers.length ? visibleUsers.map((row) => (
                    <tr key={row.user_id || row.email}>
                      <td>
                        {row.user_id != null && row.user_id !== '' ? (
                          <Link to={`/admin/users/${row.user_id}`}>{displayValue(row.email)}</Link>
                        ) : displayValue(row.email)}
                      </td>
                      <td>{displayValue(row.first_name)}</td>
                      <td>{displayValue(row.last_name)}</td>
                      <td>
                        {String(row.role ?? '').trim() ? (
                          <Link to="/help/documentation/roles">{roleLabel(row.role)}</Link>
                        ) : '—'}
                      </td>
                      {showAccount ? <td>{displayValue(row.account_name)}</td> : null}
                      <td>{displayValue(row.status ? toTitleCase(row.status) : '')}</td>
                      <td>{formatCreatedAt(row.created_at)}</td>
                      <td className="users-row-tool">
                        {row.user_id != null && row.user_id !== '' ? (
                          <Link to={`/admin/users/${row.user_id}`} aria-label={`Open ${displayValue(row.email)}`}>
                            <FontAwesomeIcon icon={faWrench} />
                          </Link>
                        ) : null}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={showAccount ? 8 : 7}>No users found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </SidebarPageLayout>
  );
}

export default Users;
